<template>
  <div v-if="user !== null"><!-- TODO show loading panel in meantime -->
    <v-layout v-if="errors.length" row>
      <v-flex lg6 offset-lg3 sm8 offset-sm2 xs12>
        <error-messages-box :messages="errors" :can-hide="true" @hide="errors = []"/>
      </v-flex>
    </v-layout>
    <v-layout row wrap>
      <v-flex xs4>
        <v-subheader>Name</v-subheader>
      </v-flex>
      <v-flex xs8>
        <v-text-field v-if="canEdit" v-model="user.name"/>
        <div v-else class="data-display">{{ user.name }}</div>
      </v-flex>
    </v-layout>
    <v-layout row wrap>
      <v-flex xs4>
        <v-subheader>Group</v-subheader>
      </v-flex>
      <v-flex xs8>
        <v-select v-if="canEdit" :items="groupLabels" v-model="user.group"/>
        <div v-else class="data-display">
          <template v-if="user.group">{{ groups[user.group] }}</template>
          <i v-else>None</i>
        </div>
      </v-flex>
    </v-layout>
    <token-manager class="buffer" v-model="user.jwtServices"
                   :servicePermissions="servicePermissions"/>
    <v-layout v-if="canEdit || isMe" row wrap>
      <v-flex xs12 md4>
        <v-subheader>
          Webcode
          <v-tooltip right>
            <v-icon slot="activator" small class="webcodeRequirements icon">help</v-icon>
            <div>
              Webcodes must contain at least:
              <ul class="webcodeRequirements">
                <li>1 lowercase letter</li>
                <li>1 uppercase letter</li>
                <li>1 number</li>
                <li>1 symbol (not alphanumeric)</li>
                <li>10 total characters</li>
              </ul>
            </div>
          </v-tooltip>
        </v-subheader>
      </v-flex>
      <v-flex xs12 md8>
        <v-layout row wrap>
          <template v-if="!!user.webcode">
            <v-flex xs10>
              <v-text-field type="password" v-model="user.webcode.webcode" :disabled="true"/>
            </v-flex>
            <v-flex xs2>
              <v-btn flat icon @click="clearWebcode">
                <v-icon>clear</v-icon>
              </v-btn>
            </v-flex>
          </template>
          <v-flex v-else xs12>
            <v-btn @click="webcode.modal = true">Set webcode</v-btn>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <div class="rule"/>
    <v-layout row wrap>
      <v-flex xs12 md4>
        <v-subheader>Additional servers</v-subheader>
      </v-flex>
      <v-flex xs12 md8>
        <v-layout row wrap>
<!--          <v-flex xs12>-->
<!--            <v-checkbox v-model="user.euCluster" label="European servers" :disabled="!canEdit"/>-->
<!--          </v-flex>-->
          <v-flex xs12>
            <v-checkbox v-model="user.developer" label="Local" :disabled="!canEdit"/>
          </v-flex>
          <v-flex xs12>
            <v-checkbox v-model="user.branchBuilder" label="Branch builder" :disabled="!canEdit"/>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout row>
      <v-flex xs12>
        <v-btn flat :to="approvalRequestsLocation">View approval requests</v-btn>
      </v-flex>
    </v-layout>
    <v-layout row class="buttonContainer">
      <v-flex xs12>
        <v-btn @click="goBack">Back</v-btn>
        <v-btn v-if="canUpdate" color="success" @click="updateUser">Update</v-btn>
      </v-flex>
    </v-layout>
    <v-dialog v-model="webcode.modal" max-width="520">
      <v-card>
        <v-card-text>
          <error-messages-box v-if="webcode.errors.length" :messages="webcode.errors"
                              :can-hide="true" @hide="webcode.errors = []"/>
          <v-text-field v-model="webcode.webcode1" :disabled="webcode.disabled" type="password"
                        label="Enter new webcode"/>
          <v-text-field v-model="webcode.webcode2" :disabled="webcode.disabled" type="password"
                        label="Enter new webcode again"/>
          <v-checkbox v-if="canHashWebcode" v-model="webcode.hashed" :disabled="webcode.disabled"
                      label="Hashed"/>
          <v-btn @click="closeWebcodeDialog" :disabled="webcode.disabled">Cancel</v-btn>
          <v-btn @click="selectWebcode" :disabled="webcode.disabled" color="success">Update</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import ErrorMessagesBox from '@/views/shared/ErrorMessagesBox';
  import TokenManager from '@/views/shared/jwtservices/TokenManager';

  export default {
    props: ['id'],
    components: { ErrorMessagesBox, TokenManager },
    data() {
      return {
        user: null,
        show: null,
        webcode: {
          modal: false,
          webcode1: '',
          webcode2: '',
          hashed: false,
          errors: [],
          disabled: false
        },
        errors: []
      };
    },
    computed: {
      groups() {
        const groups = {};
        this.$store.state.groups.groups.forEach((group) =>
          groups[group.id] = group.name
        );
        return groups;
      },
      groupLabels() {
        return Object.keys(this.groups).map((groupId) => {
          return {
            text: this.groups[groupId],
            value: groupId
          };
        });
      },
      isMe() {
        return this.authUser.sub === this.id;
      },
      // Indicates whether the logged-in user is allowed to edit non-service fields of this user
      canEdit() {
        return (this.$auth.hasPermission('EDIT_ALL_USERS')) ||
          (this.user && this.user.group &&
            this.authGroups.filter((g) => g.id === this.user.group).length);
      },
      servicePermissions() {
        const perms = this.authUser.scopes.auth.servicePermissions.slice();
        this.authGroups.filter((g) => g.id === this.user.group)
          .filter((g) => g.user === this.authUser.sub)
          .forEach((g) =>
            perms.push(...g.servicePermissions.filter((p) => perms.indexOf(p) < 0))
          );

        return perms;
      },
      canHashWebcode() {
        return this.$auth.hasPermission('EDIT_ALL_USERS');
      },
      approvalRequestsLocation() {
        return `/approvals/list?userId=${this.id}`;
      },
      canUpdate() {
        return this.canEdit || this.isMe;
      },
      ...mapState('auth', {
        authUser: 'user',
        authGroups: 'groups'
      }),
      ...mapState('users', [
        'webcodeRules'
      ])
    },
    methods: {
      goBack() {
        if (window.history.length > 1) {
          this.$router.go(-1);
        } else {
          this.$router.push('/users');
        }
      },
      clearWebcode() {
        this.user.webcode = null;
      },
      closeWebcodeDialog() {
        this.webcode = {
          ...this.webcode,
          errors: [],
          webcode1: '',
          webcode2: '',
          modal: false
        };
      },
      selectWebcode() {
        const errs = this.webcodeRules
          .filter((r) => !this.webcode.hashed && !r.pattern.test(this.webcode.webcode1))
          .map((r) => r.rule);
        if (this.webcode.webcode1 !== this.webcode.webcode2) {
          errs.push('Webcodes must match');
        }

        if (errs.length > 0) {
          this.webcode.errors = errs;
        } else {
          const submitUser = Object.assign({}, this.user);
          submitUser.webcode = {
            webcode: this.webcode.webcode1,
            hashed: this.webcode.hashed
          };
          this.webcode.disabled = true;
          this.commitUser(submitUser)
            .then(() => {
              this.webcode.disabled = false;
              this.user.webcode = {
                webcode: '********',
                hashed: false
              };
              this.closeWebcodeDialog();
            }).catch((e) => {
              this.webcode.disabled = false;
              this.$defaultHandlerHttp(e);
            });
        }
      },
      updateUser() {
        const submitUser = Object.assign({}, this.user);
        if (submitUser.webcode) {
          submitUser.webcode = null;
        } else {
          submitUser.webcode = {
            webcode: null,
            hashed: true
          };
        }
        this.commitUser(submitUser).then(() => {
          this.resetUsers(true).catch(this.$defaultHandlerHttp);
          if (this.isMe) {
            return this.$auth.refresh();
          }
        }).catch((e) => {
          if ((e.response.status === 400 || e.response.status === 403) &&
            e.response.data.error instanceof Array) {
            this.errors = e.response.data.error;
            window.scroll({
              top: 0,
              left: window.scrollX,
              behavior: 'smooth'
            });
          } else {
            this.$defaultHandlerHttp(e);
          }
        });
      },
      commitUser(user) {
        return this.$services.authenticator.put('/users/' + this.id, user).then(() => {
          this.errors = [];
          this.alert('User updated');
        });
      },
      ...mapActions('alert', {
        alert: 'show'
      }),
      ...mapActions('users', {
        resetUsers: 'initialize',
        initializeWebcodeRules: 'initializeWebcodeRules'
      })
    },
    mounted() {
      this.$services.authenticator.get('/users/' + this.id)
        .then((response) => this.user = response.data.data)
        .catch(this.$defaultHandlerHttp);

      this.initializeWebcodeRules().catch(this.$defaultHandlerHttp);
    }
  };
</script>

<style lang="less" scoped>
  @import '../../style/form.less';

  .webcodeRequirements {
    &.icon {
      margin-left: 10px;
      cursor: default;
    }

    ul& {
      padding-left: 1em;
      text-align: left;
    }
  }
</style>
